import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, ShieldCheck, Zap, Gift, Wallet, ChevronDown, CheckCircle2,
  Coffee, Smartphone, ShoppingBag, Heart, Plane, Utensils, Gamepad2, Music,
  Clock, DollarSign, Star, TrendingUp, Users, Lock, HandCoins, Timer, ChevronLeft, ChevronRight
} from "lucide-react";

const TOPICS = [
  { i: Coffee, l: "Food & drink", c: "amber" },
  { i: Smartphone, l: "Tech habits", c: "sky" },
  { i: ShoppingBag, l: "Shopping", c: "rose" },
  { i: Heart, l: "Health", c: "emerald" },
  { i: Plane, l: "Travel", c: "indigo" },
  { i: Utensils, l: "Restaurants", c: "orange" },
  { i: Gamepad2, l: "Gaming", c: "fuchsia" },
  { i: Music, l: "Music & media", c: "lime" },
];

const PAYOUT_METHODS = ["Amazon $", "PayPal cash", "Coffee card", "Visa gift", "Apple Store", "Google Play"];

const LIVE_PAYOUTS = [
  { name: "Priya S.", city: "Mumbai", amt: "$25 PayPal", ago: "2 min ago", emoji: "🎉" },
  { name: "Marco B.", city: "Milan", amt: "$10 Amazon", ago: "5 min ago", emoji: "☕️" },
  { name: "Chen L.", city: "Singapore", amt: "$50 Visa", ago: "8 min ago", emoji: "💸" },
  { name: "Ade O.", city: "Lagos", amt: "$5 Coffee", ago: "11 min ago", emoji: "🎁" },
  { name: "Sara J.", city: "Toronto", amt: "$25 PayPal", ago: "14 min ago", emoji: "🎉" },
  { name: "Kenji T.", city: "Osaka", amt: "$10 Amazon", ago: "17 min ago", emoji: "✨" },
];

const TESTIMONIALS = [
  { name: "Priya · Marketing student", quote: "I do 3-4 surveys during my commute and cash out $50 to PayPal every couple of weeks. It's the easiest side income I've found.", avatar: "P" },
  { name: "Marco · Freelance designer", quote: "Signup took a minute, no spam, and my Amazon voucher landed within an hour. Way better than the sketchy sites.", avatar: "M" },
  { name: "Chen · Product analyst", quote: "The surveys are actually short and relevant to my interests. I get 2-3 great ones a day and they take under 10 minutes each.", avatar: "C" },
];

const REWARDS = [
  { pts: 100, name: "Coffee gift card", value: "$5", note: "Starbucks, Costa, Blue Bottle", icon: Coffee },
  { pts: 500, name: "Amazon voucher", value: "$5", note: "Instant delivery", icon: ShoppingBag },
  { pts: 1000, name: "Amazon voucher", value: "$10", note: "Or Apple / Google Play", icon: ShoppingBag },
  { pts: 2500, name: "PayPal cash", value: "$25", note: "Direct to your account", icon: DollarSign },
  { pts: 5000, name: "PayPal cash", value: "$50", note: "Highest tier · fastest", icon: HandCoins },
];

const FAQS = [
  { q: "How much can I actually earn?", a: "Panelists earn on average $0.85 – $2.50 per completed survey. Most active members clear $30–$80 a month; heavy users hit $150+. It's designed as pocket money, not a full-time income." },
  { q: "How long do surveys take?", a: "Most are 4 to 12 minutes. You'll always see the length + reward before you start, so you can pick what fits your schedule." },
  { q: "When do I get paid?", a: "Rewards are added to your points wallet immediately on complete. Once you hit any redemption tier (starting at 100 pts / $5), request a payout — most Amazon / PayPal codes arrive within an hour. Coffee cards are instant." },
  { q: "Is my data safe?", a: "Yes. SyncLine Research is GDPR-compliant, never sells your PII, and only shares an anonymous ID with survey partners. You can delete your account and wipe your data anytime from the Panelist portal." },
  { q: "How do I get invited to surveys?", a: "Fill your demographic profile after signup (takes 60 seconds). We match you to studies looking for your age, location, and interests — better profile = more invites." },
  { q: "Can I use it on my phone?", a: "Absolutely. The Panelist portal is mobile-first — take surveys on the bus, in a queue, on your couch. WhatsApp / SMS survey links work seamlessly." },
];

// Scroll reveal wrapper (IntersectionObserver, expressive staggered)
function Reveal({ children, variant = "", delay = 0, stagger = false, className = "", as: Tag = "div", testId, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            // 2× rAF guarantees the initial hidden state paints before .in is applied,
            // so the CSS transition is always visible (fixes "no animation" on fast machines).
            requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
          } else {
            // re-arm when scrolling back up so the animation replays on next entry
            setVisible(false);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -140px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const base = stagger ? "reveal-stagger" : `reveal ${variant}`;
  return (
    <Tag
      ref={ref}
      data-testid={testId}
      className={`${base} ${visible ? "in" : ""} ${className}`}
      style={delay ? { "--reveal-delay": `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// Animated count-up hook
function useCountUp(target, duration = 1400) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setN(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

function CountStat({ target, suffix = "", label, testId }) {
  const n = useCountUp(target);
  return (
    <div className="text-center" data-testid={testId}>
      <div className="font-display font-extrabold text-3xl md:text-4xl bg-gradient-to-br from-white to-white/60 bg-clip-text text-transparent">
        {n.toLocaleString()}{suffix}
      </div>
      <div className="text-[11px] uppercase tracking-widest text-white/50 mt-2">{label}</div>
    </div>
  );
}

function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors" data-testid={`faq-item-${i}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-white/[0.03]" data-testid={`faq-toggle-${i}`}>
        <span className="font-medium">{q}</span>
        <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 text-sm text-white/70 leading-relaxed">{a}</div>}
    </div>
  );
}

// Earnings calculator
function EarningsCalculator() {
  const [n, setN] = useState(5);
  const avgPerSurvey = 0.85;
  const perWeek = (n * avgPerSurvey).toFixed(2);
  const perMonth = (n * 4.3 * avgPerSurvey).toFixed(0);
  const perYear = (n * 52 * avgPerSurvey).toFixed(0);
  return (
    <div className="rounded-3xl p-8 md:p-10 bg-gradient-to-br from-emerald-500/15 via-[#D4A94A]/10 to-emerald-500/[0.06] border border-[#D4A94A]/25 backdrop-blur-xl relative overflow-hidden ring-1 ring-[#D4A94A]/15" data-testid="earnings-calc">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-500/25 blur-[120px]" />
      <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-[#D4A94A]/15 blur-[100px]" />
      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] mb-4" style={{ color: "#D4A94A" }}>
          <TrendingUp className="w-3.5 h-3.5" /> Earnings calculator
        </div>
        <h3 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl leading-tight">How much could <span className="bg-gradient-to-r from-emerald-300 via-[#E7C878] to-[#D4A94A] bg-clip-text text-transparent">you</span> earn?</h3>
        <p className="text-white/60 text-sm md:text-base mt-2 mb-8">Move the slider — see your estimated monthly + annual payout.</p>
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-xs uppercase tracking-widest text-white/50">Surveys per week</span>
              <span className="font-display font-extrabold text-3xl md:text-4xl text-white" data-testid="calc-surveys-value">{n}</span>
            </div>
            <input
              type="range" min="1" max="30" value={n}
              onChange={(e) => setN(Number(e.target.value))}
              data-testid="calc-slider"
              className="w-full h-2"
              style={{ accentColor: "#D4A94A" }}
            />
            <div className="flex justify-between text-[10px] text-white/40 mt-2 font-mono"><span>1</span><span>15</span><span>30</span></div>
            <div className="mt-4 text-xs text-white/50">Average payout · <span className="font-mono" style={{ color: "#E7C878" }}>${avgPerSurvey.toFixed(2)}</span> per completed survey.</div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl p-4 bg-black/40 border border-white/10 text-center" data-testid="calc-per-week">
              <div className="text-[10px] uppercase tracking-widest text-white/50">Per week</div>
              <div className="font-display font-extrabold text-2xl text-emerald-300 mt-1">${perWeek}</div>
            </div>
            <div className="rounded-xl p-4 bg-emerald-400/15 border border-emerald-400/40 text-center scale-105 shadow-[0_0_30px_rgba(16,185,129,0.25)]" data-testid="calc-per-month">
              <div className="text-[10px] uppercase tracking-widest text-emerald-200">Per month</div>
              <div className="font-display font-extrabold text-3xl bg-gradient-to-br from-emerald-200 to-[#E7C878] bg-clip-text text-transparent mt-1">${perMonth}</div>
            </div>
            <div className="rounded-xl p-4 bg-black/40 border border-white/10 text-center" data-testid="calc-per-year">
              <div className="text-[10px] uppercase tracking-widest text-white/50">Per year</div>
              <div className="font-display font-extrabold text-2xl mt-1" style={{ color: "#D4A94A" }}>${perYear}</div>
            </div>
          </div>
        </div>
        <div className="mt-8">
          <Link to="/panel/register" className="btn-shine hover:brightness-110 inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-md shadow-[0_0_40px_rgba(16,185,129,0.35)] ring-1 ring-emerald-300/40" data-testid="calc-cta-join">
            Start earning — free signup <ArrowRight className="w-4 h-4" />
          </Link>
          <span className="ml-4 text-xs text-white/50">No credit card. Cancel anytime.</span>
        </div>
      </div>
    </div>
  );
}

// Live payout ticker
function LivePayoutTicker() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % LIVE_PAYOUTS.length), 2600);
    return () => clearInterval(t);
  }, []);
  const p = LIVE_PAYOUTS[idx];
  return (
    <div className="inline-flex items-center gap-3 bg-black/40 border border-emerald-400/30 rounded-full pl-2 pr-5 py-2 text-sm ring-1 ring-[#D4A94A]/15" data-testid="live-payouts-ticker">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-400/20 text-lg">{p.emoji}</span>
      <span className="text-white/70">
        <b className="text-white">{p.name}</b> from {p.city} just cashed out <b style={{ color: "#E7C878" }}>{p.amt}</b>
      </span>
      <span className="text-[10px] text-white/40 uppercase tracking-widest">{p.ago}</span>
    </div>
  );
}

// Testimonial rotator
function TestimonialRotator() {
  const [i, setI] = useState(0);
  const next = () => setI((x) => (x + 1) % TESTIMONIALS.length);
  const prev = () => setI((x) => (x - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  useEffect(() => {
    const t = setInterval(next, 6500);
    return () => clearInterval(t);
  }, []);
  const t = TESTIMONIALS[i];
  return (
    <div className="rounded-3xl p-10 bg-gradient-to-br from-emerald-500/10 via-[#D4A94A]/10 to-emerald-500/[0.04] border border-[#D4A94A]/20 relative overflow-hidden ring-1 ring-[#D4A94A]/10" data-testid="testimonials">
      <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-emerald-500/20 blur-[100px]" />
      <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-[#D4A94A]/20 blur-[100px]" />
      <div className="relative">
        <div className="flex gap-1 mb-4" style={{ color: "#D4A94A" }}>{[...Array(5)].map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}</div>
        <blockquote className="font-display font-bold text-xl md:text-2xl lg:text-3xl leading-tight min-h-[120px]" data-testid="testimonial-quote">
          &ldquo;{t.quote}&rdquo;
        </blockquote>
        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-400 to-[#D4A94A] flex items-center justify-center font-display font-bold text-black">{t.avatar}</div>
            <div className="text-sm">
              <div className="font-medium" data-testid="testimonial-name">{t.name}</div>
              <div className="text-white/50 text-xs">Verified panelist</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={prev} data-testid="testimonial-prev" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex gap-1">
              {TESTIMONIALS.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? "w-6" : "w-1.5 bg-white/20"}`} style={k === i ? { backgroundColor: "#D4A94A" } : undefined} />)}
            </div>
            <button onClick={next} data-testid="testimonial-next" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-ink text-white relative overflow-x-hidden" data-testid="landing-page" style={{ backgroundImage: "radial-gradient(circle at 18% 8%, rgba(16,185,129,0.14) 0%, transparent 42%), radial-gradient(circle at 82% 28%, rgba(212,169,74,0.10) 0%, transparent 46%), radial-gradient(circle at 50% 92%, rgba(190,120,120,0.06) 0%, transparent 45%)" }}>
      {/* Premium aurora blobs — deep emerald + champagne gold */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-emerald-500/18 blur-[140px]" />
        <div className="absolute top-96 -right-40 w-[500px] h-[500px] rounded-full bg-champagne/15 blur-[130px]" style={{ backgroundColor: "rgba(212,169,74,0.14)" }} />
        <div className="absolute top-[1200px] left-1/3 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px]" />
      </div>

      {/* Nav */}
      <nav className="relative z-20 bg-ink/75 backdrop-blur-xl border-b border-white/5 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 via-emerald-400 to-[#D4A94A] flex items-center justify-center shadow-[0_0_24px_rgba(212,169,74,0.35)]">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-xl">SyncLine</div>
              <div className="text-[9px] uppercase tracking-[0.32em] text-champagne/80 -mt-0.5" style={{ color: "rgba(212,169,74,0.85)" }}>Research</div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-1 text-sm">
            <a href="#how" className="text-white/60 hover:text-white px-3 py-1.5" data-testid="nav-how">How it works</a>
            <a href="#earnings" className="text-white/60 hover:text-white px-3 py-1.5" data-testid="nav-earnings">Earnings</a>
            <a href="#rewards" className="text-white/60 hover:text-white px-3 py-1.5" data-testid="nav-rewards">Rewards</a>
            <a href="#faq" className="text-white/60 hover:text-white px-3 py-1.5" data-testid="nav-faq">FAQ</a>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Link to="/panel/login" className="text-white/60 hover:text-white px-3 py-1.5" data-testid="nav-panel-login">Sign in</Link>
          <Link to="/panel/register" className="btn-shine hover:brightness-110 font-semibold px-4 py-2 rounded-md shadow-[0_0_20px_rgba(16,185,129,0.35)] ring-1 ring-emerald-300/40" data-testid="nav-join-btn">Join & earn</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-16">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 rise-in">
            <div className="mb-6" data-testid="live-payouts-wrap"><LivePayoutTicker /></div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight" data-testid="hero-headline">
              Get paid to
              <br />
              <span className="bg-gradient-to-r from-emerald-300 via-[#E7C878] to-[#D4A94A] bg-clip-text text-transparent">share your opinion.</span>
            </h1>
            <p className="text-base md:text-lg text-white/70 mt-6 max-w-xl leading-relaxed" data-testid="hero-sub">
              Join <b className="text-white">SyncLine Research</b> — take short surveys about products you already use, get paid in Amazon vouchers, PayPal cash, or coffee gift cards. Free forever. 60-second signup.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/panel/register" className="btn-shine hover:brightness-110 font-semibold px-6 py-3.5 rounded-md flex items-center gap-2 shadow-[0_0_40px_rgba(16,185,129,0.35)] ring-1 ring-emerald-300/40" data-testid="hero-cta-join">
                Join & earn — free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/panel/login" className="bg-white/5 hover:bg-white/10 border border-white/10 px-6 py-3.5 rounded-md font-medium" data-testid="hero-cta-signin">
                Panelist sign in
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-white/60" data-testid="hero-trust">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" /> GDPR-secured</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="w-4 h-4 text-[#D4A94A]" /> Instant coffee card payouts</span>
              <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-emerald-300/80" /> No spam · no PII sold</span>
            </div>
          </div>

          {/* Hero right: mock wallet card */}
          <div className="md:col-span-5 rise-in-2">
            <div className="rounded-3xl p-6 relative overflow-hidden border border-white/10 bg-gradient-to-br from-emerald-500/20 via-amber-500/10 to-rose-500/10 backdrop-blur-xl shadow-[0_20px_80px_rgba(52,211,153,0.15)]" data-testid="hero-wallet-card">
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-400/30 blur-[100px]" />
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-200/80">Points balance</div>
                    <div className="font-display font-extrabold text-5xl mt-1">2,847<span className="text-lg text-white/50 ml-1">pts</span></div>
                    <div className="text-emerald-300 text-sm font-medium mt-1">≈ $28.47 · redeemable now</div>
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-amber-400 flex items-center justify-center shadow-[0_0_30px_rgba(52,211,153,0.5)]"><Wallet className="w-6 h-6 text-black" /></div>
                </div>
                <div className="space-y-2">
                  {[
                    { n: "Mobile Habits Study", p: "+50", t: "just now", c: "emerald" },
                    { n: "Coffee Preferences", p: "+30", t: "12 min ago", c: "amber" },
                    { n: "Streaming Services", p: "+110", t: "1 hr ago", c: "rose" },
                    { n: "Grocery Shopping", p: "+40", t: "3 hr ago", c: "emerald" },
                  ].map((h, i) => (
                    <div key={i} className="flex items-center justify-between bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm hover:bg-black/50 transition-colors">
                      <div>
                        <div className="text-white">{h.n}</div>
                        <div className="text-[10px] text-white/40 mt-0.5 flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {h.t}</div>
                      </div>
                      <div className={`font-mono font-bold ${h.c === "emerald" ? "text-emerald-300" : h.c === "amber" ? "text-amber-300" : "text-rose-300"}`}>{h.p}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-xs">
                  <span className="text-white/50">Cash out in:</span>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {PAYOUT_METHODS.slice(0, 4).map((m) => (
                      <span key={m} className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/70">{m}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated stats strip */}
      <Reveal as="section" className="relative z-10 max-w-7xl mx-auto px-6 py-10" testId="stats-strip">
        <div className="rounded-3xl bg-white/[0.02] border border-white/10 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10 overflow-hidden backdrop-blur-xl ring-champagne">
          <div className="p-6"><CountStat target={240000} suffix="+" label="active panelists" testId="stat-panelists" /></div>
          <div className="p-6"><CountStat target={2400000} suffix="+ $" label="paid to panelists" testId="stat-paid" /></div>
          <div className="p-6"><CountStat target={8300} suffix="/day" label="surveys sent" testId="stat-daily" /></div>
          <div className="p-6"><CountStat target={99} suffix=".8%" label="on-time payouts" testId="stat-payouts" /></div>
        </div>
      </Reveal>

      {/* Earnings calculator */}
      <Reveal as="section" id="earnings" variant="reveal-zoom" className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <EarningsCalculator />
      </Reveal>

      {/* How it works */}
      <section id="how" className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <Reveal className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[10px] uppercase tracking-[0.32em] text-champagne font-medium" data-testid="how-eyebrow" style={{ color: "#D4A94A" }}>How it works</span>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl mt-3 leading-[0.98]">Start earning in <span className="bg-gradient-to-r from-emerald-300 to-[#D4A94A] bg-clip-text text-transparent">3 easy steps</span>.</h2>
        </Reveal>
        <Reveal stagger className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Sign up — 60 seconds", d: "Just your email + password. No credit card. No hidden fees, ever.", i: Users, c: "emerald" },
            { n: "02", t: "Take short surveys", d: "Get matched to studies from brands you already know. Each survey shows the length + reward before you start.", i: Timer, c: "amber" },
            { n: "03", t: "Cash out anytime", d: "Convert your points to Amazon, PayPal, coffee cards, or Visa gift cards — from just $5.", i: HandCoins, c: "rose" },
          ].map((s, i) => (
            <div key={i} className={`rounded-2xl p-7 relative bg-gradient-to-br from-${s.c}-500/10 to-${s.c}-500/[0.02] border border-${s.c}-500/20 hover:border-${s.c}-400/40 transition-colors`} data-testid={`how-step-${i}`}>
              <div className="absolute top-6 right-6 font-display font-extrabold text-6xl text-white/[0.06]">{s.n}</div>
              <div className={`w-12 h-12 rounded-xl bg-${s.c}-400/20 border border-${s.c}-400/40 flex items-center justify-center mb-4`}>
                <s.i className={`w-6 h-6 text-${s.c}-300`} />
              </div>
              <h3 className="font-display font-bold text-xl md:text-2xl mb-2">{s.t}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </Reveal>
      </section>

      {/* Topics you'll be asked about */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-16" data-testid="section-topics">
        <Reveal variant="reveal-left" className="max-w-2xl mb-10">
          <span className="text-[10px] uppercase tracking-[0.32em] font-medium" style={{ color: "#D4A94A" }}>Survey topics</span>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl mt-3 leading-[0.98]">Topics you'll actually enjoy.</h2>
          <p className="text-white/60 text-base md:text-lg mt-4">Tell us what interests you and we'll only invite you to studies that match. No boring questionnaires about stuff you don't care about.</p>
        </Reveal>
        <Reveal stagger className="flex flex-wrap gap-3">
          {TOPICS.map((t, i) => (
            <div key={i} className={`rounded-2xl px-5 py-4 bg-${t.c}-500/10 border border-${t.c}-500/30 hover:bg-${t.c}-500/20 hover:scale-105 transition-all cursor-default flex items-center gap-3`} data-testid={`topic-${i}`}>
              <t.i className={`w-5 h-5 text-${t.c}-300`} />
              <span className="font-medium">{t.l}</span>
            </div>
          ))}
        </Reveal>
      </section>

      {/* Rewards grid */}
      <section id="rewards" className="relative z-10 max-w-7xl mx-auto px-6 py-20" data-testid="section-rewards">
        <Reveal className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[10px] uppercase tracking-[0.32em] font-medium" style={{ color: "#D4A94A" }}>Real rewards</span>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl mt-3 leading-[0.98]">Points → <span className="bg-gradient-to-r from-emerald-300 to-[#D4A94A] bg-clip-text text-transparent">real money</span>. Fast.</h2>
          <p className="text-white/60 text-base md:text-lg mt-4">Cash out starting at just 100 pts ($5). Most payouts arrive within an hour.</p>
        </Reveal>
        <Reveal stagger className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {REWARDS.map((r, i) => (
            <div key={i} className="rounded-2xl p-5 bg-white/[0.03] border border-white/10 hover:border-[#D4A94A]/50 hover:bg-white/[0.05] transition-all group" data-testid={`reward-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400/25 to-[#D4A94A]/25 border border-[#D4A94A]/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <r.icon className="w-5 h-5 text-emerald-200" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">{r.pts} pts</div>
              <div className="font-display font-extrabold text-2xl md:text-3xl mt-1">{r.value}</div>
              <div className="text-sm text-white/80 mt-1">{r.name}</div>
              <div className="text-[10px] text-white/40 mt-1">{r.note}</div>
            </div>
          ))}
        </Reveal>
      </section>

      {/* Testimonials */}
      <Reveal as="section" variant="reveal-zoom" className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        <TestimonialRotator />
      </Reveal>

      {/* Trust & security */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-16" data-testid="section-trust">
        <Reveal stagger className="grid md:grid-cols-3 gap-6">
          {[
            { t: "Your data stays yours", d: "GDPR-compliant. We never sell PII. Delete your account anytime and we wipe everything.", i: ShieldCheck, c: "emerald" },
            { t: "No sketchy business", d: "No surveys about your bank details. No re-selling. No spam email. Ever.", i: Lock, c: "rose" },
            { t: "Payouts you can trust", d: "Real Amazon codes, real PayPal cash. On-time delivery in 99.8% of payouts.", i: HandCoins, c: "amber" },
          ].map((s, i) => (
            <div key={i} className={`rounded-2xl p-6 bg-white/[0.03] border border-white/10 hover:border-${s.c}-400/40 transition-colors`} data-testid={`trust-${i}`}>
              <div className={`w-11 h-11 rounded-xl bg-${s.c}-400/15 border border-${s.c}-400/30 flex items-center justify-center mb-4`}>
                <s.i className={`w-5 h-5 text-${s.c}-300`} />
              </div>
              <h3 className="font-display font-bold text-lg md:text-xl mb-2">{s.t}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </Reveal>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 max-w-3xl mx-auto px-6 py-20">
        <Reveal className="text-center mb-10">
          <span className="text-[10px] uppercase tracking-[0.32em] font-medium" style={{ color: "#D4A94A" }}>Questions</span>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl mt-3 leading-[0.98]">Everything you want to know.</h2>
        </Reveal>
        <Reveal stagger className="space-y-3" testId="faq-list">
          {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
        </Reveal>
      </section>

      {/* Final CTA */}
      <Reveal as="section" variant="reveal-zoom" className="relative z-10 max-w-7xl mx-auto px-6 py-16" testId="final-cta">
        <div className="rounded-3xl p-10 md:p-16 text-center relative overflow-hidden border border-[#D4A94A]/30 bg-gradient-to-br from-emerald-500/15 via-[#D4A94A]/10 to-emerald-500/[0.06] ring-1 ring-[#D4A94A]/20">
          <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-emerald-500/25 blur-[130px]" />
          <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#D4A94A]/20 blur-[130px]" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#D4A94A]/40 bg-[#D4A94A]/10 text-xs uppercase tracking-widest mb-6" style={{ color: "#F1DFA4" }} data-testid="cta-eyebrow"><Gift className="w-3 h-3" /> Free forever</span>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl lg:text-6xl leading-[0.98] max-w-3xl mx-auto">
              Your opinion is worth <span className="bg-gradient-to-r from-emerald-300 via-[#E7C878] to-[#D4A94A] bg-clip-text text-transparent">real money.</span>
            </h2>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-xl mx-auto">Join 240,000+ panelists earning on their own schedule. 60-second signup. First reward within your first week.</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/panel/register" className="btn-shine hover:brightness-110 font-semibold px-8 py-4 rounded-md flex items-center gap-2 shadow-[0_0_50px_rgba(16,185,129,0.4)] ring-1 ring-emerald-300/40" data-testid="final-cta-join">
                Join & earn — it's free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/panel/login" className="bg-white/5 hover:bg-white/10 border border-white/10 px-8 py-4 rounded-md font-medium" data-testid="final-cta-signin">
                Panelist sign in
              </Link>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 pt-14 pb-8 mt-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 via-emerald-400 to-[#D4A94A] flex items-center justify-center"><Sparkles className="w-4 h-4 text-black" /></div>
                <div className="leading-tight">
                  <div className="font-display font-extrabold text-xl">SyncLine</div>
                  <div className="text-[9px] uppercase tracking-[0.32em] -mt-0.5" style={{ color: "rgba(212,169,74,0.85)" }}>Research</div>
                </div>
              </Link>
              <p className="text-white/60 text-sm max-w-xs leading-relaxed">Earn cash for sharing your opinion. Free to join, easy to earn, safe by design.</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">Panelist</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/panel/register" className="text-white/70 hover:text-white">Join & earn</Link></li>
                <li><Link to="/panel/login" className="text-white/70 hover:text-white">Sign in</Link></li>
                <li><a href="#rewards" className="text-white/70 hover:text-white">Rewards</a></li>
                <li><a href="#faq" className="text-white/70 hover:text-white">FAQ</a></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">Operators</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/login" className="text-white/70 hover:text-white">Admin console</Link></li>
                <li><Link to="/docs/vendor" className="text-white/70 hover:text-white">Vendor API docs</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
            <div>© {new Date().getFullYear()} SyncLine Research · earn cash for your opinion</div>
            <div className="flex gap-6"><span>Privacy</span><span>Terms</span><span>GDPR</span><span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> All payouts on-time</span></div>
          </div>
        </div>
      </footer>
    </div>
  );
}
